/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useLocation } from 'js/lib/useRouter';

import EnterpriseExploreMenuItem from 'bundles/composable-page-header-enterprise/components/mobile/EnterpriseExploreMenuItem';
import EnterpriseExploreSubmenuWrapper from 'bundles/composable-page-header-enterprise/components/mobile/EnterpriseExploreSubmenuWrapper';
import type { EnterpriseLoggedInPageHeader_NaptimeManagedThirdPartyOrganizationFragment as ThirdPartyOrganization } from 'bundles/composable-page-header-enterprise/queries/__generated__/EnterpriseLoggedInMyPageHeaderQuery';
import MobileMenuConfigWrapper from 'bundles/composable-page-header/components/grid-items/mobile/MobileMenuConfigWrapper';
import AccountMenuItem from 'bundles/composable-page-header/components/mobile-menu-items/AccountMenuItem';
import AccountSubMenu from 'bundles/composable-page-header/components/mobile-menu-items/AccountSubMenu';
import GetAppMenuItem from 'bundles/composable-page-header/components/mobile-menu-items/GetAppMenuItem';
import LanguageTranslationSubMenu from 'bundles/composable-page-header/components/mobile-menu-items/LanguageTranslationSubMenu';
import MobileButton from 'bundles/composable-page-header/components/mobile-menu-items/MobileButton';
import ProgramList from 'bundles/composable-page-header/components/program-switcher/ProgramList';
import type {
  DegreeForProgramSwitcher as Degree,
  EnterpriseProgramForProgramSwitcher as EnterpriseProgram,
} from 'bundles/composable-page-header/components/program-switcher/types';
import { MOBILE_EXPLORE_IDENTIFIER, MOBILE_MAIN_IDENTIFIER } from 'bundles/composable-page-header/constants';
import getDefaultAccountDropdownLinks from 'bundles/composable-page-header/utils/getDefaultAccountDropdownLinks';
import { useIsOrgScopedHome } from 'bundles/unified-common/hooks/useIsOrgScopedHome';

import _t from 'i18n!nls/composable-page-header-enterprise';

const styles = {
  myLearningWrapper: css`
    padding-bottom: var(--cds-spacing-100);
    margin-bottom: var(--cds-spacing-100);
  `,
  borderSeparator: css`
    border-bottom: 1px solid var(--cds-color-neutral-stroke-primary-weak);
  `,
};

type Props = {
  enrolledDegreePrograms?: Array<Degree> | null;
  enrolledEnterprisePrograms?: Array<EnterpriseProgram> | null;
  chosenDegree?: Degree | null;
  chosenEnterpriseProgram?: EnterpriseProgram | null;
  hasEnrolledPrograms?: boolean;
  naptimeManagedThirdPartyOrganizations?: Array<{ slug: string; name: string }> | null;
  hasCheatingIncidents?: boolean;
  canViewEducatorAdminLink?: boolean;
  courseId?: string;
  currentProgram?: EnterpriseProgram | null;
  currentOrganization?: ThirdPartyOrganization | null;
  enrolledOrganizations?: Array<ThirdPartyOrganization> | null;
};

const DefaultLoggedInEnterpriseMobileMenuConfig = ({
  enrolledDegreePrograms,
  enrolledEnterprisePrograms,
  chosenDegree,
  chosenEnterpriseProgram,
  hasEnrolledPrograms,
  naptimeManagedThirdPartyOrganizations,
  hasCheatingIncidents,
  canViewEducatorAdminLink,
  courseId,
  currentProgram,
  currentOrganization,
  enrolledOrganizations,
}: Props) => {
  const isOrgScopedHomeEnabled = useIsOrgScopedHome();
  const location = useLocation();
  const isOnProgramScopedRoute = location.pathname.startsWith('/programs/');

  const renderMyLearningButton = (urlRoot: string, slug: string) => (
    <div css={[styles.myLearningWrapper, styles.borderSeparator]}>
      <MobileButton
        href={`/${urlRoot}/${slug}/my-learning?myLearningTab=IN_PROGRESS`}
        label={_t('My Learning')}
        name="myLearning"
      />
    </div>
  );

  const isEnrolledInCurrentProgram =
    !!currentProgram &&
    enrolledEnterprisePrograms?.some(
      (program) => program?.naptimeThirdPartyOrganization?.id === currentProgram?.naptimeThirdPartyOrganization?.id
    );

  const isEnrolledInCurrentOrganization =
    !!currentOrganization && enrolledOrganizations?.some((organization) => organization.id === currentOrganization.id);

  let myLearningButton = null;
  if (isOrgScopedHomeEnabled && !isOnProgramScopedRoute && isEnrolledInCurrentOrganization) {
    myLearningButton = renderMyLearningButton('organizations', currentOrganization?.slug);
  } else if (isEnrolledInCurrentProgram) {
    myLearningButton = renderMyLearningButton('programs', currentProgram?.slug);
  }

  return (
    <MobileMenuConfigWrapper
      subPagesMap={{
        [MOBILE_MAIN_IDENTIFIER.ACCOUNT_MENU]: (
          <AccountSubMenu
            dropdownLinks={getDefaultAccountDropdownLinks({
              managedThirdPartyOrganizations: naptimeManagedThirdPartyOrganizations,
              canViewEducatorAdminLink,
              hasCheatingIncidents,
            })}
          />
        ),
        [MOBILE_MAIN_IDENTIFIER.LANGUAGE_MENU]: <LanguageTranslationSubMenu currentCourseId={courseId} />,
        [MOBILE_MAIN_IDENTIFIER.EXPLORE_CATEGORIES_MENU]: (
          <EnterpriseExploreSubmenuWrapper
            identifier={MOBILE_EXPLORE_IDENTIFIER.ENTERPRISE_EXPLORE_CATEGORIES_SUBMENU}
          />
        ),
        [MOBILE_MAIN_IDENTIFIER.EXPLORE_SKILLS_MENU]: (
          <EnterpriseExploreSubmenuWrapper identifier={MOBILE_EXPLORE_IDENTIFIER.ENTERPRISE_EXPLORE_SKILLS_SUBMENU} />
        ),
        [MOBILE_MAIN_IDENTIFIER.EXPLORE_PROGRAM]: (
          <EnterpriseExploreSubmenuWrapper identifier={MOBILE_EXPLORE_IDENTIFIER.ENTERPRISE_EXPLORE_PROGRAM_SUBMENU} />
        ),
        [MOBILE_MAIN_IDENTIFIER.EXPLORE_MORE]: (
          <EnterpriseExploreSubmenuWrapper identifier={MOBILE_EXPLORE_IDENTIFIER.ENTERPRISE_EXPLORE_MORE_SUBMENU} />
        ),
      }}
    >
      {!isOrgScopedHomeEnabled && <AccountMenuItem />}
      {myLearningButton}
      <EnterpriseExploreMenuItem />
      {!isOrgScopedHomeEnabled && <GetAppMenuItem />}
      {!isOrgScopedHomeEnabled && hasEnrolledPrograms && (
        <ProgramList
          currentProgram={chosenEnterpriseProgram}
          currentDegree={chosenDegree}
          programs={enrolledEnterprisePrograms || []}
          degrees={enrolledDegreePrograms || []}
          alwaysShowPersonalAccount
          trackingName="switcher_dropdown_program_option"
        />
      )}
    </MobileMenuConfigWrapper>
  );
};

export default DefaultLoggedInEnterpriseMobileMenuConfig;
